import { Pipe, PipeTransform } from '@angular/core';
import { FuseResultMatch } from 'fuse.js';

const CONTEXT_BEFORE = 50;
const EXCERPT_LENGTH = 150;

/**
 * Extracts a plain-text excerpt around the best match position.
 */
@Pipe({ name: 'excerpt', standalone: true })
export class ExcerptPipe implements PipeTransform {
    transform(text: string, search: string, matches?: readonly FuseResultMatch[]): string {
        if (!text) return '';

        const index = this.getBestMatchStart(text, matches) ?? this.findIndex(text, search);

        if (index === -1) {
            const plain = this.normalizeNewlines(text);
            return plain.length > EXCERPT_LENGTH
                ? plain.slice(0, this.wordBoundary(plain, EXCERPT_LENGTH)) + '…'
                : plain;
        }

        const rawStart = Math.max(0, index - CONTEXT_BEFORE);
        const start = rawStart > 0 ? this.firstWordBoundary(text, rawStart) : 0;
        const end = Math.min(text.length, start + EXCERPT_LENGTH);

        const prefix = start > 0 ? '…' : '';
        const slice = this.normalizeNewlines(text.slice(start, end));
        const isTruncated = end < text.length;
        if (isTruncated) {
            return prefix + slice.slice(0, this.lastWordBoundary(slice)) + '…';
        }
        return prefix + slice;
    }

    private firstWordBoundary(text: string, from: number): number {
        for (let i = from; i < text.length; i++) {
            if (' .,;:!?)]}'.includes(text[i])) return i + 1;
        }
        return from;
    }

    private wordBoundary(text: string, maxLength: number): number {
        for (let i = maxLength; i < text.length; i++) {
            if (' .,;:!?)]}'.includes(text[i])) return i;
        }
        return text.length;
    }

    private lastWordBoundary(text: string): number {
        for (let i = text.length - 1; i >= 0; i--) {
            if (' .,;:!?)]}'.includes(text[i])) return i;
        }
        return text.length;
    }

    private normalizeNewlines(text: string): string {
        return text.replace(/[ \t]{0,100}\n[ \t]{0,100}/g, ' ');
    }

    private getBestMatchStart(text: string, matches: readonly FuseResultMatch[] | undefined): number | undefined {
        const match = matches?.find((m) => m.value === text);
        if (!match?.indices?.length) return undefined;
        const best = match.indices.reduce((prev, curr) => (curr[1] - curr[0] > prev[1] - prev[0] ? curr : prev));
        return best[0];
    }

    private findIndex(text: string, search: string | null | undefined): number {
        const searchTrimmed = search?.trim()?.toLowerCase();
        if (!searchTrimmed) return -1;
        return text.toLowerCase().indexOf(searchTrimmed);
    }
}
